/* eslint-disable max-lines */
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Address,
  AddressInput,
  ChannelType,
  InlineAlertType,
  State,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import {
  UpdateApplicationData,
  UpdateApplicationHandler,
} from 'features/application/hooks/useUpdateApplication';
import useSmartyStreets from 'features/signup//hooks/useSmartyStreets';
import AddressValidation from 'features/signup/Address/AddressValidation';
import { SmartyStreetsResponseItem } from 'features/signup/helpers/smartyStreets';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetStates from 'services/graphql/queries/States';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import { addressSchema } from 'shared/components/vxl/input/schemas/address.schema';
import VXLDropdown, {
  VXL_DROPDOWN_MENU_RESERVED_SPACE,
} from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLSearchField from 'shared/components/vxl/input/VXLSearchField/VXLSearchField';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useValidateAddress, {
  AddressView,
} from 'shared/hooks/useValidateAddress/useValidateAddress';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import {
  findFullStateString,
  getStateAbbreviation,
  getStateString,
} from 'shared/utils/smartyStreet/smartyStreet';
import { UseMutationState, useQuery } from 'urql';

type AddressCollectionRoute =
  '/reactivation/review-info' | '/signup/dob' | '/signup/personal-information';

interface AddressCollectionProps {
  isUpdateApplicationSuccess?: boolean;
  application?: Address | null;
  hasApplicationLoaded: boolean;
  isReapplication: boolean;
  fetching: boolean;
  error?: Error | null;
  reExecuteQuery: () => void;
  updateApplication: UpdateApplicationHandler;
  isUpdateApplicationLoading: boolean;
  updateApplicationResult: UseMutationState<UpdateApplicationData>;
  nextRoute: AddressCollectionRoute;
  backRoute: AddressCollectionRoute;
  isReactivation?: boolean;
}

const AddressCollection = ({
  application,
  hasApplicationLoaded,
  isReapplication,
  fetching,
  error,
  reExecuteQuery,
  updateApplication,
  isUpdateApplicationSuccess,
  updateApplicationResult,
  isUpdateApplicationLoading,
  nextRoute,
  backRoute,
  isReactivation = false,
}: AddressCollectionProps) => {
  const router = useRouter();
  const { t } = useTranslation('signup', { keyPrefix: 'address' });
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { bottom } = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const isKeyboardVisible = useKeyboardVisibility();

  const [showAddressFields, setShowAddressFields] = useState(false);
  const [isDropdownAddress, setIsDropdownAddress] = useState<boolean>(false);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [userEnteredAddress, setUserEnteredAddress] = useState<AddressInput>(
    {} as AddressInput,
  );

  const [searchTerms, setSearchTerms] = useState('');
  const [selectedSearchTerms, setSelectedSearchTerms] =
    useState<SmartyStreetsResponseItem>();
  const { suggestions } = useSmartyStreets(searchTerms, selectedSearchTerms);

  const {
    control,
    getValues,
    setValues,
    formState: { isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
    },
    resolver: zodResolver(addressSchema),
  });

  const [{ data: statesData }] = useQuery<{ states: State[] }>({
    query: GetStates,
    requestPolicy: 'cache-first',
  });

  const { loading: validating, addressVerification } = useValidateAddress(
    userEnteredAddress,
    ChannelType.Signup,
  );

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  useStepStartedFocusAnalytics(
    hasApplicationLoaded
      ? {
          analyticsId: 'stepStarted',
          customAttributes: {
            step_name: 'personal_info_address',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  // Populate form with existing application data if it exists
  useEffect(() => {
    const address = application;
    if (address?.addressLine1) {
      setShowAddressFields(true);
      setValues(
        {
          addressLine1: address.addressLine1 ?? '',
          addressLine2: address.addressLine2 ?? '',
          city: address.city ?? '',
          state: address.state ?? '',
          zip: address.zip ?? '',
        },
        { shouldValidate: true },
      );
    }
  }, [application, setValues]);

  useEffect(() => {
    if (
      addressVerification &&
      addressVerification?.addressType !== AddressView.FormAddress
    ) {
      Keyboard.dismiss();
      bottomSheetRef.current?.present();
    } else if (
      addressVerification !== undefined &&
      userEnteredAddress.addressLine1 !== null
    ) {
      const externalId =
        addressVerification.fullAddress?.originalAddress?.externalId;
      void updateApplicationAddress({ ...userEnteredAddress, externalId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressVerification]);

  useStepStartedFocusAnalytics(
    isUpdateApplicationSuccess
      ? {
          analyticsId: 'addressCollectionCompleted',
          customAttributes: {
            step_name: 'personal_info_address',
            ...(isReactivation && {
              reactivation_flow: true,
            }),
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  const handleAddressDropdownSelectedAnalytics = usePressableAnalytics({
    analyticsId: isManualAddress
      ? 'manualAddressSelected'
      : 'addressDropDownSelected',
    customAttributes: {
      address_selected: userEnteredAddress,
    },
  });

  useEffect(() => {
    if (userEnteredAddress && userEnteredAddress.addressLine1 !== '') {
      handleAddressDropdownSelectedAnalytics();
    }
  }, [handleAddressDropdownSelectedAnalytics, userEnteredAddress]);

  async function updateApplicationAddress(userEnteredAddress: AddressInput) {
    if (userEnteredAddress && 'addressLine1' in userEnteredAddress) {
      setUserEnteredAddress({} as AddressInput);
      bottomSheetRef.current?.close();
      setDialogLoading(false);
      await updateApplication({
        updates: {
          address: userEnteredAddress,
        },
        nextRoute: nextRoute,
      });
    }
  }

  const handleDialogConfirm = () => {
    setDialogLoading(true);
    const data =
      addressVerification?.fullAddress?.suggestedAddress ?? undefined;
    const addr = { ...data, country: 'US' } as AddressInput;
    void updateApplication({ updates: { address: addr }, nextRoute });
    bottomSheetRef.current?.close();
    setDialogLoading(false);
  };

  const handleAddressEnterManuallyAnalytics = usePressableAnalytics({
    analyticsId: 'addressEnterManually',
    customAttributes: {
      address_selected: 'user_entered',
    },
  });

  const handleAddressFormSubmittedAnalytics = usePressableAnalytics({
    analyticsId: 'addressFormSubmitted',
    customAttributes: {
      address_input_method: isDropdownAddress ? 'dropdown' : 'manual',
    },
  });

  const onSubmit = () => {
    const form = getValues();
    const addr: AddressInput = {
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      city: form.city,
      state: form.state,
      zip: form.zip,
      country: 'US',
    };
    setUserEnteredAddress(addr);
    if (!showAddressFields) {
      setShowAddressFields(true);
    }
    if (isManualAddress) {
      handleAddressEnterManuallyAnalytics();
    }
    handleAddressFormSubmittedAnalytics();
  };

  const handleBack = () => router.replace(backRoute);

  useBackHandler(handleBack);

  if (fetching || error) {
    return (
      <View style={styles.container}>
        <ScreenTransitionOverlay
          isLoading={fetching}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            error ? (
              <DefaultPageDownErrorMessage
                error={error}
                title={defaultTitle}
                message={defaultMessage}
                tryAgainAction={reExecuteQuery}
              />
            ) : null
          }
        />
      </View>
    );
  }

  return (
    <TouchableWithoutFeedback
      accessible={false}
      disabled={isWeb}
      onPress={Keyboard.dismiss}
    >
      <View style={styles.container}>
        <KeyboardAvoidingView
          style={styles.keyboard}
          behavior={isIOS ? 'padding' : 'height'}
          keyboardVerticalOffset={getKeyboardVerticalOffset(
            height,
            isKeyboardVisible,
          )}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[
              !showAddressFields ? styles.scrollViewContainer : null,
              showAddressFields ? styles.addressDropdownBottomPadding : null,
            ]}
            nestedScrollEnabled
          >
            <Stack.Screen
              options={{
                headerShown: true,
                header: (props: NativeStackHeaderProps) => (
                  <ExpoNavigationBar {...props} primaryOnPress={handleBack} />
                ),
              }}
            />
            {updateApplicationResult.error ? (
              <View style={styles.inlineAlertContainer}>
                <InlineAlert
                  alertType={InlineAlertType.Error}
                  bodyText={t('inlineAlert.errorTitle')}
                  iconKeyType="ERROR"
                  onDismiss={() => {}}
                />
              </View>
            ) : null}
            <View style={styles.form}>
              <TitleLgText>{t('header')}</TitleLgText>
              <BodyBaseText>{t('subHeader')}</BodyBaseText>
              <Controller
                control={control}
                name="addressLine1"
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => {
                  const onValueChange = (text: string) => {
                    onChange(text);
                    setSearchTerms(text);
                    setSelectedSearchTerms(undefined);
                  };
                  return (
                    <VXLSearchField
                      testID="text-input-textField.addressLine1"
                      labelText={t('textField.addressLine1')}
                      options={(suggestions ?? []).map((item, index) => ({
                        key: `${item.address_line}-${index}`,
                        label: item.address_line,
                        value: item,
                      }))}
                      keepMenuOpenOnSelect={(item) => item.entries > 1}
                      onSelectOption={(item) => {
                        if (item.entries > 1) {
                          setSelectedSearchTerms(item);
                        } else {
                          setSelectedSearchTerms(
                            {} as SmartyStreetsResponseItem,
                          );
                          setValues(
                            {
                              addressLine1: item?.street_line || '',
                              addressLine2: item?.secondary || '',
                              city: item?.city || '',
                              state: getStateAbbreviation(
                                statesData?.states ?? [],
                                item?.state ?? '',
                              ),
                              zip: item?.zipcode || '',
                            },
                            { shouldValidate: true },
                          );
                          setShowAddressFields(true);
                          setIsDropdownAddress(true);
                          onChange(item?.street_line);
                        }
                      }}
                      analyticsId="addressFieldClicked"
                      placeholder=""
                      value={value}
                      onBlur={() => {
                        setSelectedSearchTerms({} as SmartyStreetsResponseItem);
                        onBlur();
                      }}
                      onChangeText={onValueChange}
                      errorText={error?.message}
                    />
                  );
                }}
              />
              {showAddressFields ? (
                <>
                  <Controller
                    control={control}
                    name="addressLine2"
                    render={({
                      field: { onChange, onBlur, value },
                      fieldState: { error },
                    }) => (
                      <VXLTextInput
                        testID="text-input-textField.addressLine2"
                        labelText={t('textField.addressLine2')}
                        errorText={error?.message}
                        onChangeText={(text) => {
                          onChange(text);
                        }}
                        onBlur={onBlur}
                        value={value}
                        autoCapitalize="none"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="city"
                    render={({
                      field: { onChange, onBlur, value },
                      fieldState: { error },
                    }) => (
                      <VXLTextInput
                        testID="text-input-textField.city"
                        labelText={t('textField.city')}
                        errorText={error?.message}
                        onChangeText={(text) => {
                          onChange(text);
                        }}
                        onBlur={onBlur}
                        value={value}
                        autoCapitalize="words"
                      />
                    )}
                  />
                  {statesData?.states?.length ? (
                    <Controller
                      control={control}
                      name="state"
                      render={({
                        field: { onChange, onBlur, value },
                        fieldState: { error },
                      }) => (
                        <VXLDropdown
                          testID="textField.state-select"
                          labelText={t('textField.state')}
                          placeholder={t('textField.state')}
                          options={statesData.states.map(getStateString)}
                          value={
                            value
                              ? (findFullStateString(
                                  statesData.states,
                                  value,
                                ) ?? '')
                              : ''
                          }
                          errorText={error?.message}
                          onChangeText={(text) => {
                            onChange(
                              getStateAbbreviation(statesData.states, text),
                            );
                          }}
                          onBlur={onBlur}
                        />
                      )}
                    />
                  ) : null}
                  <Controller
                    control={control}
                    name="zip"
                    render={({
                      field: { onChange, onBlur, value },
                      fieldState: { error },
                    }) => (
                      <VXLTextInput
                        testID="text-input-textField.zip"
                        labelText={t('textField.zip')}
                        errorText={error?.message}
                        onChangeText={(text) => {
                          onChange(text);
                        }}
                        onBlur={onBlur}
                        value={value}
                        keyboardType="numeric"
                      />
                    )}
                  />
                </>
              ) : (
                <View style={styles.enterManuallyContainer}>
                  <Button
                    label={t('enterAddressManually')}
                    size="SMALL"
                    type="TEXT"
                    onPress={() => {
                      setIsManualAddress(true);
                      setShowAddressFields(true);
                    }}
                  />
                </View>
              )}
            </View>
          </ScrollView>
          <View
            style={[
              styles.buttonContainer,
              {
                paddingBottom: isKeyboardVisible
                  ? theme.spacing.xl
                  : bottom + theme.spacing.m,
              },
            ]}
          >
            <Button
              label={t('buttons.next')}
              onPress={onSubmit}
              isDisabled={!isValid}
              isLoading={isUpdateApplicationLoading || validating}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
          </View>
        </KeyboardAvoidingView>
        <BottomSheet ref={bottomSheetRef}>
          <AddressValidation
            handleNext={handleDialogConfirm}
            fullAddress={addressVerification?.fullAddress}
            addressType={addressVerification?.addressType}
            isLoading={dialogLoading}
            closeBottomSheet={() => bottomSheetRef.current?.close()}
          />
        </BottomSheet>
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  addressDropdownBottomPadding: {
    paddingBottom: VXL_DROPDOWN_MENU_RESERVED_SPACE,
  },
  buttonContainer: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  enterManuallyContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing.m,
  },
  form: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },

  inlineAlertContainer: {
    padding: theme.spacing.m,
  },
  keyboard: {
    flex: 1,
  },
  scrollViewContainer: {
    flex: 1,
    minHeight: 550,
  },
});

export default AddressCollection;
